import { useState, useEffect } from 'react'
import {
  Heart,
  Pencil,
  Download,
  RotateCw,
  Plus,
  UserCircle,
  Trash2,
  MoreVertical
} from 'lucide-react'
import ReplayDialog from '../chat/replay/ReplayDialog'
import SidebarEmptyState from './SidebarEmptyState'
import { apiService } from '../../services'

const Sidebar = ({ chats, onNewChat, onSelectChat, onUpdateChats, onToggleSettings }) => {
  const [editingId, setEditingId] = useState(null)
  const [editTitle, setEditTitle] = useState('')
  const [showLikedOnly, setShowLikedOnly] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [error, setError] = useState(null)
  const [openMenuId, setOpenMenuId] = useState(null)
  const [selectedChatId, setSelectedChatId] = useState(null)
  const [selectedReplayChat, setSelectedReplayChat] = useState(null)
  const [roles, setRoles] = useState([])
  const [selectedRole, setSelectedRole] = useState(localStorage.getItem('selectedRoleUuid') || '')
  const [isLoadingRoles, setIsLoadingRoles] = useState(false)
  const [collapsedGroups, setCollapsedGroups] = useState({
    today: false,
    yesterday: false,
    previousWeek: false,
    older: false
  })

  // Add reset function
  const resetState = () => {
    setEditingId(null)
    setEditTitle('')
    setError(null)
  }

  // Format timestamp for display
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  }

  // Toggle collapse state for a group
  const toggleGroupCollapse = (groupKey) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }))
  }

  // Fetch roles from API
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setIsLoadingRoles(true)
        const rolesData = await apiService.getRoles()
        // Handle the correct API response structure with 'roles' array
        const rolesList = rolesData?.roles || []
        setRoles(rolesList)
        
        // If we have roles and no role is selected, select the first one
        if (rolesList.length > 0 && !selectedRole) {
          const firstRole = rolesList[0]
          setSelectedRole(firstRole.uuid)
          localStorage.setItem('selectedRoleUuid', firstRole.uuid)
          localStorage.setItem('selectedRoleName', firstRole.role_name)
          
          // Fetch and save detailed role information for the first role
          try {
            const roleDetails = await apiService.getRoleDetails(firstRole.uuid)
            if (roleDetails) {
              localStorage.setItem('roleDetails', JSON.stringify(roleDetails))
            }
          } catch (detailsError) {
            // Failed to fetch role details silently
          }
        }
      } catch (error) {
        setError('Failed to load roles')
      } finally {
        setIsLoadingRoles(false)
      }
    }

    fetchRoles()
  }, [selectedRole])

  // Handle role selection
  const handleRoleChange = async (e) => {
    const uuid = e.target.value
    setSelectedRole(uuid)
    
    // Find the selected role object to get the role_name
    const selectedRoleObj = roles.find(role => role.uuid === uuid)
    if (selectedRoleObj) {
      // Save both uuid and role_name to localStorage
      localStorage.setItem('selectedRoleUuid', uuid)
      localStorage.setItem('selectedRoleName', selectedRoleObj.role_name)
      
      try {
        // Fetch detailed role information
        const roleDetails = await apiService.getRoleDetails(uuid)
        if (roleDetails) {
          // Save the complete role details to localStorage
          localStorage.setItem('roleDetails', JSON.stringify(roleDetails))
        }
      } catch (error) {
        // Failed to fetch role details silently
      }
    }
  }

  // Group chats by date
  const groupChatsByDate = (chats) => {
    const now = new Date()
    const today = new Date(now)
    today.setHours(0, 0, 0, 0)
    
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    
    const lastWeek = new Date(today)
    lastWeek.setDate(lastWeek.getDate() - 7)

    return chats.reduce((groups, chat) => {
      const chatDate = new Date(chat.date)
      chatDate.setHours(0, 0, 0, 0)

      // For testing/demo data with future dates, treat them as today
      if (chatDate > now) {
        groups.today.push(chat)
      }
      // For actual dates
      else if (isSameDay(chatDate, today)) {
        groups.today.push(chat)
      } else if (isSameDay(chatDate, yesterday)) {
        groups.yesterday.push(chat)
      } else if (chatDate >= lastWeek && chatDate < yesterday) {
        groups.previousWeek.push(chat)
      } else {
        groups.older.push(chat)
      }
      return groups
    }, { today: [], yesterday: [], previousWeek: [], older: [] })
  }

  // Helper function to compare dates
  const isSameDay = (date1, date2) => {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate()
  }

  const handleRename = (chatId, newTitle) => {
    if (newTitle.trim()) {
      onUpdateChats(prev => prev.map(chat => 
        chat.id === chatId ? { ...chat, title: newTitle.trim() } : chat
      ))
    }
    resetState()
  }

  const handleLike = async (chatId) => {
    const chat = chats.find(c => c.id === chatId);
    if (!chat) return;

    const newLikedStatus = !chat.isLiked;

    // Update UI optimistically
    onUpdateChats(prev => prev.map(c =>
      c.id === chatId ? { ...c, isLiked: newLikedStatus } : c
    ));

    // Update via API
    try {
      await apiService.updateThread(chat.threadId, { isLiked: newLikedStatus });
    } catch (error) {
      console.error('[Sidebar] Failed to update like status:', error);
      // Revert on error
      onUpdateChats(prev => prev.map(c =>
        c.id === chatId ? { ...c, isLiked: !newLikedStatus } : c
      ));
    }

    resetState();
  };

  const [isExporting, setIsExporting] = useState(false);

  const exportChats = async () => {
    try {
      setIsExporting(true);

      // Fetch full thread data for each chat from the API
      const fullChats = await Promise.all(
        chats.map(async (chat) => {
          try {
            const fullThread = await apiService.getThread(chat.threadId);
            return {
              ...chat,
              messages: fullThread.messages || [],
            };
          } catch (err) {
            console.warn(`Failed to fetch thread ${chat.threadId}:`, err);
            return chat; // Return partial data if fetch fails
          }
        })
      );

      const dataStr = JSON.stringify(fullChats, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
      const exportFileDefaultName = `chat-history-${new Date().toISOString().split('T')[0]}.json`;

      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
      resetState();
    } catch (error) {
      console.error('Export failed:', error);
      setError('Failed to export chats');
    } finally {
      setIsExporting(false);
    }
  };

  // Filter and group chats
  // Note: API threads have messageCount, not messages array
  let filteredChats = showLikedOnly
    ? chats.filter(chat => chat.isLiked)
    : chats.filter(chat => (chat.messages && chat.messages.length > 0) || chat.messageCount > 0);

  // Apply search filter if there's a search query
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase();
    filteredChats = filteredChats.filter(chat =>
      chat.title?.toLowerCase().includes(query) ||
      chat.messages?.some(msg => msg.content?.toLowerCase().includes(query))
    );
  }

  const groupedChats = groupChatsByDate(filteredChats)

  const handleReplayClick = (chat) => {
    // Pass the complete chat object to ReplayDialog
    setSelectedReplayChat(chat);
  };

  const handleNewChatClick = () => {
    // Call the onToggleSettings function which now creates a new thread
    onToggleSettings();
    
    // Focus the input field after a short delay
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('focusChatInput'));
    }, 100);
  };

  const handleNewChat = async () => {
    try {
      // First check if we should make the API call or if the parent will handle it
      const shouldCreateThread = localStorage.getItem('sidebarHandlesThreadCreation') === 'true';
      
      if (shouldCreateThread) {
        try {
          // Call the API to create a new thread
          const response = await apiService.createThread();
          
          // Extract the threadId from the response, checking both possible properties
          const threadId = response.id || response.threadId;
          
          if (!threadId) {
            throw new Error('No threadId in API response');
          }
          
          // Save the threadId to localStorage
          localStorage.setItem('currentThreadId', threadId);
          
          // Call the parent's onNewChat with the threadId
          onNewChat(threadId);
        } catch (apiError) {
          // Let the parent component handle thread creation as fallback
          onNewChat();
        }
      } else {
        // Let the parent component handle thread creation
        onNewChat();
      }
      
      // Dispatch event to focus the input field
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('focusChatInput'));
      }, 100);
    } catch (error) {
      // If API call fails, still create a new chat
      onNewChat();
      
      // Still try to focus the input field
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('focusChatInput'));
      }, 100);
    }
  };


  // Add this to the component initialization
  useEffect(() => {
    // Set a flag to indicate that the Sidebar should handle thread creation
    localStorage.setItem('sidebarHandlesThreadCreation', 'true');

    // Filter out duplicate roles to ensure unique keys
    const deduplicatedRoles = roles.reduce((acc, role) => {
      // If we haven't seen this UUID yet, add it to the accumulator
      if (!acc.some(r => r.uuid === role.uuid)) {
        acc.push(role);
      }
      return acc;
    }, []);

    // If there are duplicates, update the roles state with the deduplicated array
    if (deduplicatedRoles.length !== roles.length) {
      setRoles(deduplicatedRoles);
    }

    return () => {
      // Clean up when component unmounts
      localStorage.removeItem('sidebarHandlesThreadCreation');
    };
  }, [roles]);

  // Add function to clear history but keep liked chats
  const handleClearHistoryKeepLiked = async () => {
    // Get all liked chats and non-liked chats
    const likedChats = chats.filter(chat => chat.isLiked === true);
    const chatsToDelete = chats.filter(chat => chat.isLiked !== true);

    // Delete non-liked threads via API
    for (const chat of chatsToDelete) {
      try {
        await apiService.deleteThread(chat.threadId);
        console.log('[Sidebar] Deleted thread:', chat.threadId);
      } catch (error) {
        console.error('[Sidebar] Failed to delete thread:', chat.threadId, error);
      }
    }

    // Update parent components with only the liked chats
    onUpdateChats(likedChats);

    // Notify other components
    window.dispatchEvent(new CustomEvent('chatHistoryUpdated', {
      detail: { chats: likedChats }
    }));

    // Show confirmation message
    setError("Chat history cleared (liked chats kept)");
    setTimeout(() => setError(null), 3000);
  };

  const ChatGroup = ({ title, chats, groupKey }) => {
    if (!chats?.length) return null
    
    const isCollapsed = collapsedGroups[groupKey]
    
    return (
      <div className="mb-4">
        <button
          onClick={() => toggleGroupCollapse(groupKey)}
          className="w-full px-3 py-2 text-sm font-bold text-gray-700 flex items-center justify-between hover:bg-gray-200 rounded bg-gray-100"
        >
          <span>{title}</span>
          <span className={`transform transition-transform ${isCollapsed ? 'rotate-0' : 'rotate-90'}`}>
            ›
          </span>
        </button>
        {!isCollapsed && (
          <div className="space-y-1 bg-gray-50 rounded-b animate-fadeIn">
          {chats.map((chat) => {
            const isKeyboardSelected = chat.id === selectedChatId;
            return (
            <div
              key={chat.id}
              className={`group flex items-center px-3 py-3 hover:bg-gray-100 rounded-lg transition-colors ${
                isKeyboardSelected ? 'bg-blue-100 ring-2 ring-blue-500' : ''
              } ${chat.isLiked ? 'bg-red-50 border-l-2 border-l-red-400' : ''}`}
            >
              {editingId === chat.id ? (
                <form 
                  className="flex-1 flex items-center"
                  onSubmit={(e) => {
                    e.preventDefault()
                    handleRename(chat.id, editTitle)
                  }}
                >
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="flex-1 px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    autoFocus
                    onBlur={() => handleRename(chat.id, editTitle)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        resetState()
                      }
                    }}
                  />
                </form>
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <button
                      onClick={() => {
                        console.log('[Sidebar] Chat item clicked, threadId:', chat.threadId);
                        onSelectChat(chat.threadId);
                        // Focus the input field when a chat is selected
                        setTimeout(() => {
                          window.dispatchEvent(new CustomEvent('focusChatInput'));
                        }, 100);
                      }}
                      className="w-full text-left focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-1 py-0.5"
                      aria-label={`Open chat: ${chat.title}`}
                    >
                      {/* Title row with like indicator */}
                      <div className="flex items-center gap-2 mb-1">
                        {chat.isLiked && (
                          <Heart className="h-3.5 w-3.5 text-red-500 flex-shrink-0 fill-current" />
                        )}
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {chat.title}
                        </span>
                      </div>

                      {/* Preview of last message */}
                      {chat.messages && chat.messages.length > 0 && chat.messages[chat.messages.length - 1]?.content && (
                        <div className="text-xs text-gray-500 truncate mb-0.5">
                          {chat.messages[chat.messages.length - 1].content.substring(0, 50)}
                          {chat.messages[chat.messages.length - 1].content.length > 50 ? '...' : ''}
                        </div>
                      )}

                      {/* Metadata: message count and time */}
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <span>{chat.messages?.length || 0} message{chat.messages?.length !== 1 ? 's' : ''}</span>
                        <span>•</span>
                        <span>{formatTime(chat.date)}</span>
                      </div>
                    </button>
                  </div>

                  {/* Kebab menu - always visible */}
                  <div className="relative flex-shrink-0 ml-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenuId(openMenuId === chat.id ? null : chat.id);
                      }}
                      className="p-1.5 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                      aria-label="Chat options"
                      aria-expanded={openMenuId === chat.id}
                      aria-haspopup="true"
                    >
                      <MoreVertical className="h-4 w-4 text-gray-600" />
                    </button>

                    {/* Dropdown menu */}
                    {openMenuId === chat.id && (
                      <>
                        {/* Backdrop to close menu when clicking outside */}
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setOpenMenuId(null)}
                          aria-hidden="true"
                        />

                        {/* Menu content */}
                        <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                          <button
                            onClick={() => {
                              handleReplayClick(chat);
                              setOpenMenuId(null);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                          >
                            <RotateCw className="h-4 w-4" />
                            Replay
                          </button>
                          <button
                            onClick={() => {
                              setEditingId(chat.id);
                              setEditTitle(chat.title);
                              setOpenMenuId(null);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                          >
                            <Pencil className="h-4 w-4" />
                            Rename
                          </button>
                          <button
                            onClick={() => {
                              handleLike(chat.id);
                              setOpenMenuId(null);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                          >
                            <Heart className={`h-4 w-4 ${chat.isLiked ? 'text-red-500 fill-current' : ''}`} />
                            {chat.isLiked ? 'Unlike' : 'Like'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
            );
          })}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-gray-100 border-r border-gray-300 w-80 flex-shrink-0">
      <div className="p-4 flex flex-col gap-4 bg-gray-200">
        {/* Search Bar */}
        <div className="relative">
          <label htmlFor="chat-search" className="sr-only">
            Search chat history
          </label>
          <input
            id="chat-search"
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search chats..."
            className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            aria-label="Search chat history"
            aria-describedby="search-results-count"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
              aria-label="Clear search"
            >
              Clear
            </button>
          )}
        </div>

        <div className="flex items-center justify-between">
          <button
            onClick={() => setShowLikedOnly(!showLikedOnly)}
            className={`flex items-center gap-1 text-sm ${
              showLikedOnly ? 'text-red-600' : 'text-gray-600'
            }`}
          >
            {showLikedOnly ? (
              <Heart className="h-4 w-4 fill-current" />
            ) : (
              <Heart className="h-4 w-4" />
            )}
            <span>{showLikedOnly ? 'Showing liked' : 'Show liked'}</span>
          </button>

          <div className="flex gap-2">
            <button
              onClick={handleClearHistoryKeepLiked}
              className="p-1 text-gray-600 hover:bg-gray-200 rounded"
              title="Clear History (Keep Liked)"
            >
              <Trash2 className="h-5 w-5" />
            </button>
            <button
              onClick={exportChats}
              disabled={isExporting}
              className={`p-1 text-gray-600 hover:bg-gray-200 rounded ${isExporting ? 'opacity-50 cursor-wait' : ''}`}
              title={isExporting ? "Exporting..." : "Export chats"}
            >
              <Download className={`h-5 w-5 ${isExporting ? 'animate-pulse' : ''}`} />
            </button>
            <button
              onClick={handleNewChatClick}
              className="p-1 text-gray-600 hover:bg-gray-200 rounded"
              title="New Chat"
            >
              <Plus className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="px-4 py-2 text-sm text-red-600 bg-red-100">
          {error}
        </div>
      )}

      {/* Search results count */}
      {searchQuery && filteredChats.length > 0 && (
        <div
          id="search-results-count"
          className="px-4 py-2 text-sm text-gray-700 bg-blue-50 border-b border-blue-100 flex items-center justify-between"
          role="status"
          aria-live="polite"
        >
          <span>
            Found <span className="font-semibold text-blue-900">{filteredChats.length}</span> chat{filteredChats.length !== 1 ? 's' : ''} matching &quot;{searchQuery}&quot;
          </span>
          <button
            onClick={() => setSearchQuery('')}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-1"
            aria-label="Clear search results"
          >
            Clear
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto bg-gray-50">
        {filteredChats.length === 0 ? (
          <SidebarEmptyState showLikedOnly={showLikedOnly} searchQuery={searchQuery} />
        ) : (
          <>
            <ChatGroup title="Today" chats={groupedChats.today} groupKey="today" />
            <ChatGroup title="Yesterday" chats={groupedChats.yesterday} groupKey="yesterday" />
            <ChatGroup title="Previous 7 days" chats={groupedChats.previousWeek} groupKey="previousWeek" />
            <ChatGroup title="Older" chats={groupedChats.older} groupKey="older" />
          </>
        )}
      </div>

      {selectedReplayChat && (
        <ReplayDialog
          chat={selectedReplayChat}
          onClose={() => setSelectedReplayChat(null)}
          onReplay={(threadId) => {
            setSelectedReplayChat(null);
            onSelectChat(threadId);
          }}
        />
      )}
    </div>
  );
};

export default Sidebar; 